import React, { useEffect, useState } from 'react';
import { Gift, Loader2, RefreshCw } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  canAdminCancelSubscription,
  canAdminRequestRefundForOrder,
  countAdminAccountPendingOrders,
  filterAdminAccountRecentOrders,
  formatAdminAccountDateTime,
  formatAdminAccountMoney,
  formatAdminAccountPaymentPlatform,
  formatAdminAccountSubscriptionDateLabel,
  getAdminRefundActionLabel,
  getAdminAccountUserLabel,
  hasAdminAccountSubscriptionStatusMismatch,
  resolveAdminAccountSubscriptionDateValue,
  type AdminAccountPaymentDebugResponse,
  type AdminAccountReferralDebugResponse,
  type AdminAccountReferralPointTransaction,
  type AdminAccountSubscriptionEvent,
  type AdminAccountReferralUsage,
  type AdminAccountPaymentSummaryResponse,
  type AdminAccountUser,
} from '../../types/account';
import {
  resolveAdminPaymentDisplayStatus,
  formatSubscriptionStatus,
  getStatusBadgeVariant,
  type AdminPaymentDisplayStatusInput,
} from './paymentStatusLabels';
import { formatRefundRequestStatus, isAdminRefundRequestInProgress } from './refundStatusLabels';
import { SubscriptionCancelPanel, type SubscriptionCancelChoice } from '../../components/SubscriptionCancelPanel';

export type AccountActionState = {
  loadingKey: string | null;
  error: string | null;
  message: string | null;
};

export type ManualBenefitGrantOptions = {
  durationDays: number;
  planCode: string;
  reason: string;
};

export const formatCompactText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

export function formatBillingCycle(value: string | null | undefined): string {
  if (!value) {
    return '주기 없음';
  }

  switch (value.toUpperCase()) {
    case 'MONTHLY':
      return '월 구독';
    case 'YEARLY':
      return '연 구독';
    default:
      return value;
  }
}

export { formatSubscriptionStatus, getStatusBadgeVariant } from './paymentStatusLabels';

function PaymentStatusBadge({
  input,
  planCode,
}: {
  input: AdminPaymentDisplayStatusInput;
  planCode?: string | null;
}) {
  const display = resolveAdminPaymentDisplayStatus(input);

  return (
    <div className="inline-flex min-w-0 max-w-full flex-col gap-0.5">
      <Badge variant={display.variant}>{display.label}</Badge>
      {(planCode || display.detail) && (
        <span className="break-all text-xs text-muted-foreground">
          {[planCode, display.detail].filter(Boolean).join(' · ')}
        </span>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid min-w-0 grid-cols-[84px_minmax(0,1fr)] gap-2 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className={mono ? 'min-w-0 break-all font-mono text-foreground' : 'min-w-0 break-all text-foreground'}>
        {value}
      </div>
    </div>
  );
}

export function ExpandedAccountDetails({
  account,
  paymentSummary,
  paymentDebug,
  referralDebug,
  loading,
  referralLoading,
  error,
  referralError,
  actionState,
  showSubscriptionEvents = true,
  onRequestRefund,
  onCancelSubscription,
  onReloadReferralDebug,
  onResetReferralUsedHistory,
  onResetReferralReceivedHistory,
  onResetFirstLoginTrial,
  onGrantManualBenefit,
}: {
  account: AdminAccountUser;
  paymentSummary?: AdminAccountPaymentSummaryResponse;
  paymentDebug: AdminAccountPaymentDebugResponse | null;
  referralDebug: AdminAccountReferralDebugResponse | null;
  loading: boolean;
  referralLoading: boolean;
  error: string | null;
  referralError: string | null;
  actionState: AccountActionState;
  showSubscriptionEvents?: boolean;
  onRequestRefund: (userId: string, orderId: string) => Promise<void>;
  onCancelSubscription: (userId: string, subscriptionId: number | string, choice: SubscriptionCancelChoice) => Promise<void>;
  onReloadReferralDebug?: (userId: string) => Promise<void>;
  onResetReferralUsedHistory?: (userId: string, email: string) => Promise<void>;
  onResetReferralReceivedHistory?: (userId: string, email: string) => Promise<void>;
  onResetFirstLoginTrial?: (userId: string, email: string) => Promise<void>;
  onGrantManualBenefit?: (userId: string, email: string, options: ManualBenefitGrantOptions) => Promise<void>;
}) {
  const [showPendingOrders, setShowPendingOrders] = useState(false);
  const [manualBenefitForm, setManualBenefitForm] = useState({
    durationDays: '30',
    planCode: 'SAJU_PRO',
    reason: '버그 제보 감사',
  });

  useEffect(() => {
    setShowPendingOrders(false);
  }, [account.id, paymentDebug?.userId]);

  const hiddenPendingOrderCount = paymentDebug ? countAdminAccountPendingOrders(paymentDebug.recentOrders) : 0;
  const manualBenefitDays = Math.max(1, Math.min(365, Number(manualBenefitForm.durationDays) || 30));
  const visibleRecentOrders = paymentDebug
    ? filterAdminAccountRecentOrders(paymentDebug.recentOrders, showPendingOrders)
    : [];
  const activeSubscription = paymentDebug?.activeSubscription ?? null;
  const currentPaymentGroup = paymentSummary?.appGroups.find((group) => group.activeSubscriptionCount > 0) ?? null;
  const currentAccessIsGranted = [
    'COUPON',
    'REFERRAL_REWARD',
    'FIRST_LOGIN_BENEFIT',
    'MANUAL_BENEFIT',
    'OTHER_BENEFIT',
  ].includes(currentPaymentGroup?.currentAccessSource ?? '');
  const isGrantedBenefit = currentAccessIsGranted
    || /^\[(COUPON|MANUAL|REFERRAL|BENEFIT|ADMIN)(?::|\])/i.test(activeSubscription?.lastPaymentResult || '');
  const activeSubscriptionPlatform = isGrantedBenefit
    ? '혜택 지급'
    : formatAdminAccountPaymentPlatform(activeSubscription?.paymentPlatform);
  const activeSubscriptionDateLabel = isGrantedBenefit
    ? '혜택 종료'
    : activeSubscription?.paymentPlatform?.toUpperCase() === 'GOOGLE_PLAY'
      ? 'Google 이용 기준일'
      : formatAdminAccountSubscriptionDateLabel(activeSubscription?.status);
  const effectiveSubscriptionDate = activeSubscription
    ? resolveAdminAccountSubscriptionDateValue(
        activeSubscription.status,
        activeSubscription.nextBillingDate,
        activeSubscription.endDate,
      )
    : null;
  const hasSubscriptionStatusMismatch = hasAdminAccountSubscriptionStatusMismatch(activeSubscription);
  const timelineEvents = paymentDebug
    ? [...paymentDebug.recentSubscriptionEvents].sort((left, right) => {
        const leftValue = left.createdAt || left.newEndDate || '';
        const rightValue = right.createdAt || right.newEndDate || '';
        return leftValue.localeCompare(rightValue);
      })
    : [];
  const lifecycleEvents = timelineEvents
    .filter((event) => isStoreLifecycleEvent(event.eventType))
    .reverse();
  const durationTimelineEvents = timelineEvents
    .filter((event) => !isStoreLifecycleEvent(event.eventType));

  return (
    <div className="space-y-5 px-1 py-4 sm:px-3 sm:py-5">
      {actionState.error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {actionState.error}
        </div>
      )}
      {actionState.message && !actionState.error && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {actionState.message}
        </div>
      )}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] 2xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="min-w-0 space-y-4">
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">계정 정보</div>
            <div className="grid gap-2">
              <DetailRow label="이메일" value={formatCompactText(account.email)} />
              <DetailRow label="사용자명" value={formatCompactText(account.username)} />
              <DetailRow label="표시 이름" value={getAdminAccountUserLabel(account)} />
              <DetailRow label="계정 ID" value={formatCompactText(account.id)} mono />
              <DetailRow label="권한" value={account.roles.length > 0 ? account.roles.join(', ') : '-'} />
              <DetailRow label="가입일" value={formatAdminAccountDateTime(account.createdDate)} />
              <DetailRow label="최근 로그인" value={formatAdminAccountDateTime(account.lastLoginDate)} />
            </div>
          </section>

          <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">앱별 구독 요약</div>
            {!paymentSummary || paymentSummary.appGroups.length === 0 ? (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                구독 요약이 없습니다.
              </div>
            ) : (
              <div className="space-y-3">
                {paymentSummary.appGroups.map((group) => (
                  <div key={group.appKey} className="rounded-lg border bg-background p-2.5 sm:p-3">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <Badge variant={group.appKey.toUpperCase() === 'SAJU' ? 'default' : 'secondary'}>
                        {group.appLabel}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        활성 {group.activeSubscriptionCount}건 / 전체 {group.summaries.length}건
                      </span>
                    </div>
                    <div className="space-y-2">
                      {group.summaries.map((item, index) => (
                        <div
                          key={`${group.appKey}-${item.productPlanCode || item.planLabel}-${index}`}
                          className="rounded-md border bg-muted/20 px-3 py-2.5"
                        >
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className="break-words font-medium text-foreground">
                              {item.planLabel || item.productPlanCode || '플랜 없음'}
                            </span>
                            {item.productPlanCode && (
                              <span className="break-all font-mono text-xs text-muted-foreground">
                                {item.productPlanCode}
                              </span>
                            )}
                            {item.primary && <Badge variant="outline">대표</Badge>}
                            <PaymentStatusBadge
                              input={{
                                status: item.status,
                                failureCategory: item.failureCategory,
                                context: 'summary',
                              }}
                              planCode={item.productPlanCode || undefined}
                            />
                          </div>
                          <div className="mt-2 grid gap-x-3 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
                            <div>주기: {formatBillingCycle(item.billingCycle)}</div>
                            <div>플랫폼: {formatAdminAccountPaymentPlatform(item.paymentPlatform)}</div>
                            <div>{formatAdminAccountSubscriptionDateLabel(item.status)}: {formatAdminAccountDateTime(item.expiresAt)}</div>
                            <div>앱: {group.appLabel}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {onReloadReferralDebug ? <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold tracking-tight">추천인 모니터링</div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-2"
                disabled={referralLoading}
                onClick={() => void onReloadReferralDebug(account.id)}
              >
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${referralLoading ? 'animate-spin' : ''}`} />
                새로고침
              </Button>
            </div>

            {referralError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {referralError}
              </div>
            ) : referralLoading && !referralDebug ? (
              <div className="flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                추천인 상태를 불러오는 중입니다...
              </div>
            ) : referralDebug ? (
              <div className="space-y-3 rounded-lg border bg-background p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{formatReferralTier(referralDebug.stats?.tier || referralDebug.currentConfig?.tier)}</Badge>
                  <Badge variant={referralDebug.referralCode ? 'default' : 'secondary'}>
                    {referralDebug.referralCode ? `내 코드 ${referralDebug.referralCode.code}` : '내 코드 없음'}
                  </Badge>
                  <Badge variant="secondary">포인트 {referralDebug.pointBalance}P</Badge>
                </div>

                <div className="grid gap-2">
                  <DetailRow
                    label="초대 현황"
                    value={`${referralDebug.stats?.inviteCount ?? 0} / ${referralDebug.stats?.maxInvites ?? 0} · 남은 ${referralDebug.stats?.remainingInvites ?? 0}`}
                  />
                  <DetailRow
                    label="현재 보상"
                    value={formatReferralRewardSummary(referralDebug)}
                  />
                  <DetailRow
                    label="코드 생성"
                    value={formatAdminAccountDateTime(referralDebug.referralCode?.createdAt)}
                  />
                </div>

                <ReferralUsageGroup
                  title="사용한 이력"
                  emptyMessage="이 계정이 사용한 추천인 이력이 없습니다."
                  items={referralDebug.usageAsReferee}
                  counterpartLabel="추천인"
                  counterpartResolver={(usage) => usage.referrerId}
                />
                <ReferralUsageGroup
                  title="사용당한 이력"
                  emptyMessage="이 계정 코드가 사용된 이력이 없습니다."
                  items={referralDebug.usageAsReferrer}
                  counterpartLabel="피추천인"
                  counterpartResolver={(usage) => usage.refereeId}
                />
                <ReferralPointTransactionGroup items={referralDebug.recentPointTransactions} />
              </div>
            ) : (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                추천인 상태 데이터가 없습니다.
              </div>
            )}
          </section> : null}

          {onResetReferralUsedHistory && onResetReferralReceivedHistory ? <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">추천인 테스트</div>
            <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 p-3">
              <p className="text-sm text-amber-950">
                추천인 사용 이력과 사용당한 이력을 따로 리셋할 수 있습니다. 이미 지급된 Pro 기간은 자동 롤백되지 않을 수 있습니다.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={actionState.loadingKey === `referral-reset-used:${account.id}`}
                  onClick={() => void onResetReferralUsedHistory(account.id, account.email)}
                >
                  {actionState.loadingKey === `referral-reset-used:${account.id}` && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  사용한 이력 리셋
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={actionState.loadingKey === `referral-reset-received:${account.id}`}
                  onClick={() => void onResetReferralReceivedHistory(account.id, account.email)}
                >
                  {actionState.loadingKey === `referral-reset-received:${account.id}` && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  사용당한 이력 리셋
                </Button>
              </div>
            </div>
          </section> : null}

          {onResetFirstLoginTrial ? (
            <section className="space-y-3 rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold tracking-tight">첫 로그인 혜택 테스트</div>
              <div className="rounded-lg border border-sky-300/60 bg-sky-50/60 p-3">
                <p className="text-sm text-sky-950">
                  첫 로그인 혜택 지급 이력만 리셋합니다. 이미 지급된 Pro 기간은 자동 롤백되지 않습니다.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={actionState.loadingKey === `first-login-trial-reset:${account.id}`}
                    onClick={() => void onResetFirstLoginTrial(account.id, account.email)}
                  >
                    {actionState.loadingKey === `first-login-trial-reset:${account.id}` && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    첫 로그인 혜택 지급 이력 리셋
                  </Button>
                </div>
              </div>
            </section>
          ) : null}

          {onGrantManualBenefit ? (
            <section className="space-y-3 rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold tracking-tight">수동 혜택 지급</div>
              <div className="rounded-lg border border-emerald-300/60 bg-emerald-50/60 p-3">
                <p className="text-sm text-emerald-950">
                  버그 제보 감사나 운영 보상으로 해당 계정에 Pro 기간을 즉시 지급합니다.
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-[120px_140px_minmax(0,1fr)]">
                  <label className="space-y-1.5">
                    <span className="text-xs font-medium text-emerald-950">일수</span>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={manualBenefitForm.durationDays}
                      onChange={(event) =>
                        setManualBenefitForm((current) => ({ ...current, durationDays: event.target.value }))
                      }
                    />
                  </label>
                  <label className="space-y-1.5">
                    <span className="text-xs font-medium text-emerald-950">플랜</span>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      value={manualBenefitForm.planCode}
                      onChange={(event) =>
                        setManualBenefitForm((current) => ({ ...current, planCode: event.target.value }))
                      }
                    >
                      <option value="SAJU_PRO">SAJU_PRO</option>
                      <option value="SAJU_PLUS">SAJU_PLUS</option>
                      <option value="SAJU_PERSONAL">SAJU_PERSONAL</option>
                    </select>
                  </label>
                  <label className="space-y-1.5">
                    <span className="text-xs font-medium text-emerald-950">사유</span>
                    <Input
                      value={manualBenefitForm.reason}
                      onChange={(event) =>
                        setManualBenefitForm((current) => ({ ...current, reason: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={actionState.loadingKey === `manual-benefit-grant:${account.id}`}
                    onClick={() =>
                      void onGrantManualBenefit(account.id, account.email, {
                        durationDays: manualBenefitDays,
                        planCode: manualBenefitForm.planCode,
                        reason: manualBenefitForm.reason.trim() || '관리자 수동 혜택',
                      })
                    }
                  >
                    {actionState.loadingKey === `manual-benefit-grant:${account.id}` ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Gift className="mr-2 h-4 w-4" />
                    )}
                    {manualBenefitDays}일 혜택 지급
                  </Button>
                </div>
              </div>
            </section>
          ) : null}
        </div>

        <div className="min-w-0 space-y-4">
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">현재 구독 상태</div>
            {loading ? (
              <div className="flex items-center gap-2 rounded-md border px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                결제 상세를 불러오는 중입니다...
              </div>
            ) : error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            ) : activeSubscription ? (
              <div className="rounded-lg border bg-background p-2.5 sm:p-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <PaymentStatusBadge
                    input={{
                      status: activeSubscription.status,
                      lastPaymentResult: activeSubscription.lastPaymentResult,
                      orderFailReason: activeSubscription.orderFailReason,
                      failureCategory: activeSubscription.failureCategory,
                      context: 'subscription',
                    }}
                    planCode={activeSubscription.productPlanCode}
                  />
                  <span className="break-words font-medium text-foreground">
                    {activeSubscription.productName || activeSubscription.productPlanCode || '활성 구독'}
                  </span>
                  {activeSubscription.refundPending && <Badge variant="warning">환불 처리 중 — 승인되면 구독 종료</Badge>}
                </div>
                <div className="mt-3 grid gap-x-3 gap-y-2 sm:grid-cols-2">
                  <DetailRow label="Current plan" value={paymentDebug?.currentPlanCode || '-'} mono />
                  <DetailRow label="Product" value={formatCompactText(activeSubscription.productName)} />
                  <DetailRow label="Plan code" value={formatCompactText(activeSubscription.productPlanCode)} mono />
                  <DetailRow label="플랫폼" value={activeSubscriptionPlatform} />
                  <DetailRow label="Start" value={formatAdminAccountDateTime(activeSubscription.startDate)} />
                  <DetailRow
                    label={activeSubscriptionDateLabel}
                    value={formatAdminAccountDateTime(effectiveSubscriptionDate)}
                  />
                </div>
                {hasSubscriptionStatusMismatch && (
                  <div className="mt-3 rounded-md border border-amber-300/60 bg-amber-50/70 px-3 py-3">
                    <div className="text-sm font-semibold text-amber-950">상태 불일치</div>
                    <p className="mt-1 text-sm text-amber-950">
                      저장 상태는 만료지만 실제 접근 만료일은 남아 있습니다.
                    </p>
                    <div className="mt-2 text-sm text-amber-900">
                      실제 접근 만료: {formatAdminAccountDateTime(effectiveSubscriptionDate)}
                    </div>
                  </div>
                )}
                {durationTimelineEvents.length > 0 && (
                  <SubscriptionTimelineGroup items={durationTimelineEvents} />
                )}
                {canAdminCancelSubscription(activeSubscription) && (
                  <div className="mt-3">
                    <SubscriptionCancelPanel
                      allowPeriodEnd
                      allowImmediate={activeSubscription.paymentPlatform === 'PADDLE'}
                      defaultReason="관리자 확인 후 해지"
                      busy={actionState.loadingKey === `cancel:${activeSubscription.id}`}
                      onConfirm={(choice) => onCancelSubscription(account.id, activeSubscription.id, choice)}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                현재 활성 구독이 없습니다.
              </div>
            )}
          </section>

          {showSubscriptionEvents ? <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">결제·구독 이벤트</div>
            {loading ? (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                결제·구독 이벤트를 불러오는 중입니다...
              </div>
            ) : error ? (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                결제·구독 이벤트를 가져오지 못했습니다.
              </div>
            ) : lifecycleEvents.length > 0 ? (
              <SubscriptionLifecycleEventGroup items={lifecycleEvents} />
            ) : (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                기록된 결제·구독 변화가 없습니다.
              </div>
            )}
          </section> : null}

          <section className="space-y-3 rounded-lg border bg-card p-4">
            <div className="text-sm font-semibold tracking-tight">구독 기록</div>
            {loading ? (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                결제 기록을 불러오는 중입니다...
              </div>
            ) : error ? (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                구독 기록을 가져오지 못했습니다.
              </div>
            ) : paymentDebug ? (
              <div className="space-y-2">
                {hiddenPendingOrderCount > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                    <span>대기 중인 주문 {hiddenPendingOrderCount}건이 숨김 처리되어 있습니다.</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPendingOrders((current) => !current)}
                    >
                      {showPendingOrders ? '대기 주문 숨기기' : '대기 주문 보기'}
                    </Button>
                  </div>
                )}
                {visibleRecentOrders.length > 0 ? visibleRecentOrders.map((order) => (
                  <div key={order.orderId || String(order.id)} className="rounded-lg border bg-background p-2.5 sm:p-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="break-words font-medium text-foreground">
                        {order.productName || order.productPlanCode || `Order ${order.id}`}
                      </span>
                      {order.paymentPlatform?.trim().toUpperCase() === 'GOOGLE_PLAY'
                        && ['PAID', 'COMPLETED'].includes(order.status.trim().toUpperCase()) ? (
                          <div className="inline-flex min-w-0 max-w-full flex-col gap-0.5">
                            <Badge variant="success">Google 구독 등록</Badge>
                            <span className="break-all text-xs text-muted-foreground">
                              {order.productPlanCode} · 실제 청구·무료체험 여부는 Play 원본 상태로 구분
                            </span>
                          </div>
                        ) : (
                          <PaymentStatusBadge
                            input={{
                              status: order.status,
                              failReason: order.failReason,
                              failureCategory: order.failureCategory,
                              context: 'order',
                            }}
                            planCode={order.productPlanCode}
                          />
                        )}
                    </div>
                    <div className="mt-3 grid gap-x-3 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
                      <div>주문 ID: {order.orderId || String(order.id)}</div>
                      <div>플랜 코드: {formatCompactText(order.productPlanCode)}</div>
                      <div>결제 플랫폼: {formatAdminAccountPaymentPlatform(order.paymentPlatform)}</div>
                      <div>
                        {order.paymentPlatform?.trim().toUpperCase() === 'GOOGLE_PLAY' ? '상품 기준가' : '결제 금액'}:{' '}
                        {formatAdminAccountMoney(order.amount, order.currency)}
                      </div>
                      <div>통화: {formatCompactText(order.currency)}</div>
                      <div>주기: {formatBillingCycle(order.billingCycle)}</div>
                      <div>생성일: {formatAdminAccountDateTime(order.createdAt)}</div>
                      {order.refundRequestStatus && (
                        <div>환불 상태: {formatRefundRequestStatus(order.refundRequestStatus)}{order.refundRequestedAt ? ` · 요청 ${formatAdminAccountDateTime(order.refundRequestedAt)}` : ''}{isAdminRefundRequestInProgress(order.refundRequestStatus) && <Badge variant="warning" className="ml-2">환불 처리 중</Badge>}</div>
                      )}
                    </div>
                    {canAdminRequestRefundForOrder(order, activeSubscription) && order.orderId && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto"
                          disabled={actionState.loadingKey === `refund:${order.orderId}`}
                          onClick={() => {
                            if (order.orderId) {
                              void onRequestRefund(account.id, order.orderId);
                            }
                          }}
                        >
                          {actionState.loadingKey === `refund:${order.orderId}` && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {getAdminRefundActionLabel(order)}
                        </Button>
                      </div>
                    )}
                  </div>
                )) : (
                  <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                    {hiddenPendingOrderCount > 0
                      ? '확정된 결제 기록이 없습니다. 필요하면 대기 주문을 펼쳐 확인하세요.'
                      : '최근 결제 기록이 없습니다.'}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                최근 결제 기록이 없습니다.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function formatReferralTier(value: string | null | undefined): string {
  if (!value) return '티어 없음';

  switch (value.toLowerCase()) {
    case 'early':
      return '초기';
    case 'growth':
      return '성장기';
    case 'scale':
      return '확장기';
    case 'mature':
      return '포화기';
    default:
      return value;
  }
}

function formatReferralRewardSummary(
  debug: Pick<AdminAccountReferralDebugResponse, 'currentConfig'>,
): string {
  const config = debug.currentConfig;
  if (!config) {
    return '현재 추천 보상 정보가 없습니다.';
  }

  const rewards: string[] = [];
  if (config.referrerDays > 0) rewards.push(`추천인 Pro ${config.referrerDays}일`);
  if (config.refereeDays > 0) rewards.push(`피추천인 Pro ${config.refereeDays}일`);
  if (config.referrerPoints > 0) rewards.push(`추천인 ${config.referrerPoints}P`);
  if (config.refereePoints > 0) rewards.push(`피추천인 ${config.refereePoints}P`);

  return rewards.length > 0 ? rewards.join(', ') : '현재 추천 보상이 없습니다.';
}

function formatPointTransactionAmount(value: number): string {
  return `${value > 0 ? '+' : ''}${value}P`;
}

function formatSubscriptionTimelineDelta(value: number | null): string {
  if (value === null || value === 0) {
    return '변동 없음';
  }
  return `${value > 0 ? '+' : ''}${value}일`;
}

function formatSubscriptionEventType(value: string | null | undefined): string {
  if (!value) return '이벤트';

  switch (value.toUpperCase()) {
    case 'REFERRAL_BONUS':
      return '추천인 보상';
    case 'EXTENSION':
      return '기간 연장';
    case 'PURCHASE':
      return '구매';
    case 'RENEWAL':
      return '갱신';
    case 'CANCELLATION':
      return '취소';
    case 'STORE_PURCHASE_VERIFIED':
      return 'Google 구매 검증';
    case 'STORE_OFFER_STARTED':
      return 'Offer 무료체험 시작';
    case 'STORE_SUBSCRIPTION_CANCELED':
      return 'Google 구독 해지';
    case 'STORE_SUBSCRIPTION_EXPIRED':
      return 'Google 구독 만료';
    case 'STORE_RENEWED':
      return 'Google 구독 갱신';
    case 'STORE_PAYMENT_FAILED':
      return 'Google 결제 문제';
    case 'STORE_REVOKED':
      return 'Google 구독 회수';
    case 'STORE_STATE_CHANGED':
      return 'Google 상태 변경';
    default:
      return value;
  }
}

function isStoreLifecycleEvent(value: string | null | undefined): boolean {
  return value?.trim().toUpperCase().startsWith('STORE_') ?? false;
}

function ReferralUsageGroup({
  title,
  emptyMessage,
  items,
  counterpartLabel,
  counterpartResolver,
}: {
  title: string;
  emptyMessage: string;
  items: AdminAccountReferralUsage[];
  counterpartLabel: string;
  counterpartResolver: (usage: AdminAccountReferralUsage) => string;
}) {
  return (
    <section className="space-y-2">
      <div className="text-sm font-semibold tracking-tight">{title}</div>
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((usage) => (
            <div key={usage.id} className="rounded-md border bg-muted/20 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm text-foreground">{usage.code}</span>
                <Badge variant={getStatusBadgeVariant(usage.status)}>
                  {formatSubscriptionStatus(usage.status)}
                </Badge>
              </div>
              <div className="mt-2 grid gap-x-3 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
                <div>{counterpartLabel}: {counterpartResolver(usage)}</div>
                <div>사용 시각: {formatAdminAccountDateTime(usage.usedAt)}</div>
                <div>Usage ID: {usage.id}</div>
                <div>보상 반영: {formatAdminAccountDateTime(usage.proGrantedAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SubscriptionTimelineGroup({
  items,
}: {
  items: AdminAccountSubscriptionEvent[];
}) {
  return (
    <section className="mt-4 space-y-2">
      <div className="text-sm font-semibold tracking-tight">기간 누적 타임라인</div>
      <div className="space-y-2">
        {items.map((event) => (
          <div key={`${event.id}`} className="rounded-md border bg-muted/20 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={event.deltaDays && event.deltaDays > 0 ? 'success' : 'outline'}>
                {formatSubscriptionTimelineDelta(event.deltaDays)}
              </Badge>
              <span className="break-words text-sm font-medium text-foreground">
                {event.description || formatSubscriptionEventType(event.eventType)}
              </span>
            </div>
            <div className="mt-2 grid gap-x-3 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
              <div>유형: {formatSubscriptionEventType(event.eventType)}</div>
              <div>발생 시각: {formatAdminAccountDateTime(event.createdAt)}</div>
              <div>이전 만료: {formatAdminAccountDateTime(event.previousEndDate)}</div>
              <div>새 만료: {formatAdminAccountDateTime(event.newEndDate)}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SubscriptionLifecycleEventGroup({
  items,
}: {
  items: AdminAccountSubscriptionEvent[];
}) {
  return (
    <div className="space-y-2">
      {items.map((event) => {
        const eventType = event.eventType?.trim().toUpperCase() ?? '';
        const isProblem = [
          'STORE_SUBSCRIPTION_CANCELED',
          'STORE_SUBSCRIPTION_EXPIRED',
          'STORE_PAYMENT_FAILED',
          'STORE_REVOKED',
        ].includes(eventType);
        return (
          <div key={`${event.id}`} className="rounded-md border bg-muted/20 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={isProblem ? 'warning' : 'success'}>
                {formatSubscriptionEventType(event.eventType)}
              </Badge>
              <span className="break-words text-sm font-medium text-foreground">
                {event.description || formatSubscriptionEventType(event.eventType)}
              </span>
            </div>
            <div className="mt-2 grid gap-x-3 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2">
              <div>발생 시각: {formatAdminAccountDateTime(event.createdAt)}</div>
              {event.newEndDate ? (
                <div>이용 기준일: {formatAdminAccountDateTime(event.newEndDate)}</div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReferralPointTransactionGroup({
  items,
}: {
  items: AdminAccountReferralPointTransaction[];
}) {
  return (
    <section className="space-y-2">
      <div className="text-sm font-semibold tracking-tight">추천인 포인트 트랜잭션</div>
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
          추천인 관련 포인트 트랜잭션이 없습니다.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((transaction) => (
            <div key={transaction.id} className="rounded-md border bg-muted/20 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-sm font-semibold ${transaction.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatPointTransactionAmount(transaction.amount)}
                </span>
                <Badge variant="outline">{transaction.transactionType}</Badge>
              </div>
              <div className="mt-2 grid gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <div>{transaction.description || '설명 없음'}</div>
                <div>발생 시각: {formatAdminAccountDateTime(transaction.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
